import { useCallback, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Link,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useLicense } from "../../../store/license/hooks";
import { useVersion } from "../../../store/version/hooks";
import { ContentTitleBar } from "../../../components/ContentTitleBar";
import { WaitDialog } from "../../../components/dialogs";
import {
  CloudUpload,
  ContentCopy,
  InfoOutlined,
  WarningAmber,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";

const copyrightSymbol = "\u00A9";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const copyToClipboard = (status) => {
  navigator.clipboard.writeText(status.systemSerialNumber);
};

export const LicensePage = () => {
  const theme = useTheme();
  const { isLoading, isSaving, status, uploadLicense } = useLicense();
  const {
    loadVersion,
    isLoaded: isVersionLoaded,
    value: version,
  } = useVersion();

  const mailString =
    "mailto:support@wavesciencescorp.com?subject=AF License Request&body=Serial Number: " +
    status.systemSerialNumber;

  const fiRef = useRef();

  const load = useCallback(() => {
    loadVersion();
  }, [loadVersion]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Stack component="main" height={1}>
      <WaitDialog open={isLoading || isSaving} />
      <Stack
        flex={1}
        gap={2}
        p={2}
        pb={18}
        sx={{ overflowY: "auto", overscrollBehavior: "contain" }}
      >
        <Paper elevation={4} sx={{ p: 2 }}>
          <Grid
            container
            rowSpacing={4}
            columnSpacing={2}
            justifyContent={{ md: "space-between" }}
          >
            <Grid
              size={{ xs: 6, sm: 4, md: 2 }}
              display="flex"
              flexDirection="column"
              alignItems="flex-start"
            >
              <Typography variant="caption">License Status</Typography>
              <Chip
                sx={{
                  backgroundColor: !status.licenseValid ? "#bf0000" : "#00b976",
                  color: "white",
                }}
                label={status.licenseValid ? "LICENSED" : "NOT LICENSED"}
              />
            </Grid>
            {status.licenseValid && (
              <>
                <Grid
                  size={{ xs: 6, sm: 4, md: 2 }}
                  display="flex"
                  flexDirection="column"
                  alignItems="flex-start"
                >
                  <Typography variant="caption">License Type</Typography>
                  <Typography variant="h6">{status.licenseType}</Typography>
                </Grid>
                <Grid
                  size={{ xs: 6, sm: 4, md: 2 }}
                  display="flex"
                  flexDirection="column"
                  alignItems="flex-start"
                >
                  <Typography variant="caption">Number of Seats</Typography>
                  <Typography variant="h6">
                    {status.licenseType !== "SEAT" ? "Unlimited" : status.seats}
                  </Typography>
                </Grid>
                <Grid
                  size={{ xs: 6, sm: 4, md: 2 }}
                  display="flex"
                  flexDirection="column"
                  alignItems="flex-start"
                >
                  <Typography variant="caption">Channels</Typography>
                  <Typography variant="h6">
                    {status.channels === 0 ? "Unlimited" : status.channels}
                  </Typography>
                </Grid>
                <Grid
                  size={{ xs: 6, sm: 4, md: 2 }}
                  display="flex"
                  flexDirection="column"
                  alignItems="flex-start"
                >
                  <Typography variant="caption">Expiration Date</Typography>
                  <Typography variant="h6">
                    {status.expirationDateEpochMs
                      ? new Date(
                          status.expirationDateEpochMs,
                        ).toLocaleDateString()
                      : "NEVER"}
                  </Typography>
                </Grid>
              </>
            )}
            <Grid size={{ xs: 12 }}>
              <Grid container spacing={3}>
                <Grid
                  size={{ xs: 6, md: 3 }}
                  display="flex"
                  flexDirection="column"
                >
                  <Typography variant="caption">Serial Number</Typography>
                  <Typography
                    variant="subtitle2"
                    sx={{ wordBreak: "break-word" }}
                  >
                    {status.systemSerialNumber}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6, md: 9 }} alignSelf="center">
                  <Tooltip title="Copy to Clipboard">
                    <Button>
                      <ContentCopy onClick={() => copyToClipboard(status)} />
                    </Button>
                  </Tooltip>
                </Grid>
              </Grid>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Button
                component="label"
                variant="contained"
                startIcon={<CloudUpload />}
              >
                Upload License
                <VisuallyHiddenInput
                  ref={fiRef}
                  type="file"
                  onChange={(e) => {
                    if (e.target.files.length > 0) {
                      const file = e.target.files[0];
                      console.log(file);
                      const loader = new FileReader();
                      loader.onload = function (loadEvent) {
                        if (loadEvent.target.readyState !== 2) return;
                        if (loadEvent.target.error) {
                          alert(
                            "Error while reading file " +
                              file.name +
                              ": " +
                              loadEvent.target.error,
                          );
                          return;
                        }
                        uploadLicense(loadEvent.target.result);
                      };
                      loader.readAsText(file);

                      fiRef.current.value = "";
                    }
                  }}
                />
              </Button>
            </Grid>
          </Grid>
        </Paper>
        <Paper
          elevation={4}
          sx={{
            backgroundColor: status.licenseValid
              ? theme.palette.primary.dark
              : "#ed6c02",
            color: theme.palette.common.white,
            display: "flex",
            alignItems: "center",
            p: 2,
          }}
        >
          {status.licenseValid && <InfoOutlined />}
          {!status.licenseValid && <WarningAmber />}
          <Stack ml={2}>
            {!status.licenseValid && (
              <Typography variant="body1">
                This system is currently not licensed. To request a license,
                copy the serial number above and send your request in an email
                to{" "}
                <Link
                  href={mailString}
                  sx={{ cursor: "pointer" }}
                  underline="hover"
                  color="inherit"
                >
                  support@wavesciencescorp.com
                </Link>
                .
              </Typography>
            )}
            {status.licenseValid && (
              <Typography variant="body1">
                To request a change in the license, copy the serial number above
                and send your request in an email to{" "}
                <Link
                  href={mailString}
                  sx={{ cursor: "pointer" }}
                  underline="hover"
                  color="inherit"
                >
                  support@wavesciencescorp.com
                </Link>
                .
              </Typography>
            )}
          </Stack>
        </Paper>
        <Paper elevation={4} sx={{ p: 2 }}>
          <Grid container spacing={1}>
            <Grid size={12}>
              <Typography variant="body1" gutterBottom>
                This product uses the following libraries.
              </Typography>
              <Typography variant="subtitle2">LAPACK</Typography>
              <Typography variant="body2">
                Copyright {copyrightSymbol} 1992-2013 The University of
                Tennessee and The University of Tennessee Research Foundation.
                All rights reserved.
              </Typography>
              <Typography variant="body2">
                Copyright {copyrightSymbol} 2000-2013 The University of
                California Berkeley. All rights reserved.
              </Typography>
              <Typography variant="body2">
                Copyright {copyrightSymbol} 2006-2013 The University of Colorado
                Denver. All rights reserved.
              </Typography>
            </Grid>
            <Grid size={12}>
              <Typography variant="subtitle2">OpenBLAS</Typography>
              <Typography variant="body2">
                Copyright {copyrightSymbol} 2011-2014 The OpenBLAS Project. All
                rights reserved.
              </Typography>
            </Grid>
            <Grid size={12}>
              <Typography variant="subtitle2">FFTW</Typography>
              <Typography variant="body2">
                Copyright {copyrightSymbol} 2003, 2007-11 Matteo Frigo
              </Typography>
              <Typography variant="body2">
                Copyright {copyrightSymbol} 2003, 2007-11 Massachusetts
                Institute of Technology.
              </Typography>
            </Grid>
          </Grid>
        </Paper>
        <Paper
          elevation={4}
          sx={{
            backgroundColor: theme.palette.primary.dark,
            color: theme.palette.common.white,
            p: 2,
          }}
        >
          <Grid container spacing={1}>
            <Grid display="flex" alignItems="center" minHeight={24} size={12}>
              <Typography variant="body2">
                Acoustical Focus Version:{" "}
                {isVersionLoaded ? (
                  version
                ) : (
                  <CircularProgress
                    size={12}
                    sx={{ color: "inherit", ml: 0.5 }}
                  />
                )}
              </Typography>
            </Grid>
            <Grid display="flex" alignItems="center" minHeight={24} size={12}>
              <Typography variant="body2">
                {copyrightSymbol} 2023-2026 Wave Sciences, LLC. All rights
                reserved.
              </Typography>
            </Grid>
            <Grid display="flex" alignItems="center" minHeight={24} size={12}>
              <Typography variant="body2">
                The Wave Sciences logo, Acoustical Focus, and GLIMPSE are
                registered trademarks of Wave Sciences, LLC.
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      </Stack>
      <Box position="fixed" bottom={0} width={1} zIndex={1}>
        <ContentTitleBar label="License" />
      </Box>
    </Stack>
  );
};
